import React, { Component, Fragment } from 'react';
import { DatePicker, message } from 'antd';
import cx from 'classnames';
import locale from 'antd/locale/ko_KR';
import dayjs from 'dayjs';
import Modal from '../../Modal';
import Button from '../../Button';
import moment, { setDateFormat } from '../../../lib/moment';
import { comparePrePropsAndNextProps } from '../../../lib/common';
import { setDateRangePickerBtnText } from '../../../lib/utils/commonUtils';
import {
  DATE_FORMAT,
  DATE_PICKER_DEFAULT_BUTTON_TEXT,
  DATE_PICKER_DEFAULT_PLACEHOLDER_TEXT,
} from '../../../constants/service/common/utilConstants';
import './DateRangePicker.scss';

// moment와 dayjs 간 변환 유틸리티
const momentToDayjs = (m) => (m ? dayjs(m.valueOf()) : null);
const dayjsToMoment = (d) => (d ? moment(d.valueOf()) : null);

class DateRangePicker extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isOpen: false,
      endOpen: false,
      startDate: props.value && props.value.startDate ? moment(props.value.startDate) : null,
      endDate: props.value && props.value.endDate ? moment(props.value.endDate) : null,
    };
  }

  /**
   * Props 값이 변경이 되었을 때, state 의 값을 변경
   */
  UNSAFE_componentWillReceiveProps(props) {
    if (
      comparePrePropsAndNextProps([
        [
          [
            props.value ? moment(props.value.startDate).valueOf() : null,
            props.value ? moment(props.value.endDate).valueOf() : null,
          ],
          [moment(this.state.startDate).valueOf(), moment(this.state.endDate).valueOf()],
        ],
      ])
    ) {
      this.setState({
        startDate: props.value && props.value.startDate ? moment(props.value.startDate) : null,
        endDate: props.value && props.value.endDate ? moment(props.value.endDate) : null,
      });
    }
  }

  /**
   *  날짜 선택하는 모달을 열고 닫는 토글
   *  모달을 닫을 경우에는 시작일과 종료일을
   *  변경하기전의 값으로 변경함
   */
  toggleModal = () => {
    const { value } = this.props;
    const { isOpen } = this.state;

    this.setState({
      isOpen: !isOpen,
      endOpen: false,
      startDate: value && value.startDate ? moment(value.startDate) : null,
      endDate: value && value.endDate ? moment(value.endDate) : null,
    });
  };

  /**
   * outsideRange 와 endDate 값에 따라서
   * startDate 를 선택하지 못하는 날짜 설정
   * antd v5에서 DatePicker의 disabledDate는 dayjs 객체를 받음
   */
  disabledStartDate = (startDateDayjs) => {
    const { endDate } = this.state;
    const { outsideRange, showTime } = this.props;
    if (!startDateDayjs || !endDate) return false;

    const startDate = startDateDayjs;
    const endDateDayjs = momentToDayjs(endDate);

    let condition = startDate.startOf('day').valueOf() > endDateDayjs.startOf('day').valueOf();

    /**
     *  outsideRange 가 존재한다면, before 제한 추가
     */
    if (outsideRange)
      condition =
        condition || startDate.startOf(showTime ? 'second' : 'day').valueOf() < dayjs(outsideRange.before).valueOf();
    return condition;
  };

  /**
   * outsideRange 와 startDate 값에 따라서
   * endDate 를 선택하지 못하는 날짜 설정
   */
  disabledEndDate = (endDateDayjs) => {
    const { startDate } = this.state;
    const { outsideRange } = this.props;
    if (!endDateDayjs || !startDate) return false;

    const startDateDayjsVal = momentToDayjs(startDate);

    let condition = endDateDayjs.startOf('day').valueOf() < startDateDayjsVal.startOf('day').valueOf();

    /**
     *  outsideRange 가 존재한다면, before 제한 추가
     */
    if (outsideRange) condition = condition || endDateDayjs.valueOf() > dayjs(outsideRange.after).valueOf();
    return condition;
  };

  /**
   * startDate onChange callback
   * startDate 가 endDate 보다 빠를 경우에는
   * startDate 에 endDate 값을 대입한다.
   * antd v5에서 onChange는 dayjs 객체를 반환
   */
  onStartChange = (startDateDayjs) => {
    const { endDate } = this.state;
    let startDateMoment = dayjsToMoment(startDateDayjs);
    if (startDateMoment && startDateMoment.isAfter(endDate)) startDateMoment = endDate;

    this.setState({
      startDate: startDateMoment,
    });
  };

  /**
   * endDate onChange callback
   * endDate 가 startDate 보다 빠를 경우에는
   * endDate 에 startDate 값을 대입한다.
   */
  onEndChange = (endDateDayjs) => {
    const { startDate } = this.state;
    let endDateMoment = dayjsToMoment(endDateDayjs);
    if (endDateMoment && endDateMoment.isBefore(startDate)) endDateMoment = startDate;
    this.setState({
      endDate: endDateMoment,
    });
  };

  handleStartOpenChange = (open) => {
    if (!open) {
      this.setState({ endOpen: true });
    }
  };

  handleEndOpenChange = (open) => {
    this.setState({ endOpen: open });
  };

  /**
   * 최종적으로 날짜를 선택시 callback
   */
  onSelect = () => {
    const { onSelect, outFormat = DATE_FORMAT.YMD_DASH_FORMAT } = this.props;
    const { isOpen, startDate, endDate } = this.state;

    /**
     *  시작 날짜와 종료 날짜를 선택했는지 체크
     */
    if (!startDate || !endDate) {
      message.error('시작 날짜와 종료 날짜를 선택해주세요.');
      return;
    }

    this.setState({
      isOpen: !isOpen,
      endOpen: false,
    });

    onSelect({
      startDate: setDateFormat(startDate, outFormat),
      endDate: setDateFormat(endDate, outFormat),
    });
  };

  render() {
    const { isOpen, startDate, endDate, endOpen } = this.state;
    const {
      isEditable = true,
      btnTextFormat,
      startText = DATE_PICKER_DEFAULT_PLACEHOLDER_TEXT,
      endText = DATE_PICKER_DEFAULT_PLACEHOLDER_TEXT,
      format = 'LL',
      showTime = false,
      className,
    } = this.props;

    /**
     *  버튼 텍스트 문구에 들어갈 선택된 날짜 데이터 또는 기본 데이터
     */
    const btnText =
      startDate && endDate
        ? setDateRangePickerBtnText(btnTextFormat, startDate, endDate)
        : DATE_PICKER_DEFAULT_BUTTON_TEXT;

    // moment를 dayjs로 변환하여 DatePicker에 전달
    const startDateDayjs = momentToDayjs(startDate);
    const endDateDayjs = momentToDayjs(endDate);

    return (
      <Fragment>
        <Modal
          title="날짜 선택하기"
          isOpen={isOpen}
          width={610}
          height={460}
          isFooter={true}
          overflow="hidden"
          onOk={() => this.onSelect()}
          destroyOnClose={true}
          onClose={() => this.toggleModal()}
        >
          <div className="date-range-picker-modal">
            <DatePicker
              locale={locale}
              disabledDate={this.disabledStartDate}
              showTime={showTime}
              format={format}
              value={startDateDayjs}
              placeholder={startText}
              onChange={this.onStartChange}
              onOpenChange={this.handleStartOpenChange}
              allowClear={false}
            />

            <span className="dash">~</span>

            <DatePicker
              locale={locale}
              disabledDate={this.disabledEndDate}
              showTime={showTime}
              format={format}
              value={endDateDayjs}
              placeholder={endText}
              onChange={this.onEndChange}
              open={endOpen}
              onOpenChange={this.handleEndOpenChange}
              allowClear={false}
            />
          </div>
        </Modal>

        {/* 날짜를 선택하는 모달을 열고 닫는 버튼*/}
        <Button
          className={cx('date-range-picker-button', className)}
          color="light"
          disabled={!isEditable}
          text={btnText}
          onClick={() => this.toggleModal()}
        />
      </Fragment>
    );
  }
}

export default DateRangePicker;
